import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import EventCard from "@/components/base/EventCard";
import useSEO from "@/hooks/useSEO";
import { events } from "@/mocks/events";

const origin = typeof window !== "undefined" ? window.location.origin : "";

export default function EventDetail() {
  const { id } = useParams();
  const event = events.find((item) => item.id === id);
  const related = event
    ? event.related
        .map((relId) => events.find((item) => item.id === relId))
        .filter((item): item is (typeof events)[number] => Boolean(item))
    : [];

  const [copied, setCopied] = useState(false);

  useSEO({
    title: event ? `${event.title} | 爆料专区事件档案` : "档案不存在 | 爆料专区",
    description: event
      ? event.summary
      : "该事件档案未找到，可以返回爆料专区档案库，按主题与年份继续浏览。",
    keywords: event
      ? `${event.title},${event.categoryName},爆料专区,年度盘点,${event.tags.join(",")}`
      : "爆料专区,事件档案",
    canonical: `/event/${id}`,
    ogType: "article",
    image: event?.cover,
    jsonLd: event
      ? [
          {
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            headline: event.title,
            description: event.summary,
            image: [event.cover],
            datePublished: event.date,
            dateModified: event.date,
            articleSection: event.categoryName,
            keywords: event.tags.join(","),
            inLanguage: "zh-CN",
            author: { "@type": "Organization", name: "爆料专区" },
            publisher: { "@type": "Organization", name: "爆料专区" },
            mainEntityOfPage: { "@type": "WebPage", "@id": `${origin}/event/${event.id}` },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: `${origin}/` },
              { "@type": "ListItem", position: 2, name: "年度盘点", item: `${origin}/archive` },
              {
                "@type": "ListItem",
                position: 3,
                name: event.categoryName,
                item: `${origin}/archive?category=${event.category}`,
              },
              {
                "@type": "ListItem",
                position: 4,
                name: event.title,
                item: `${origin}/event/${event.id}`,
              },
            ],
          },
        ]
      : undefined,
  });

  const copyLink = () => {
    const url = `${origin}/event/${id}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      });
    } else {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!event) {
    return (
      <section className="w-full">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-20 md:px-6 md:py-28">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            这份档案暂时找不到
          </h1>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
            它可能已经调整了地址，或者还没有整理完成。可以回到档案库，按主题和年份继续翻找。
          </p>
          <Link
            to="/archive"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line" />
            返回档案库
          </Link>
        </div>
      </section>
    );
  }

  return (
    <article className="w-full">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-6 md:py-12">
          <Breadcrumbs
            items={[
              { label: "首页", to: "/" },
              { label: "年度盘点", to: "/archive" },
              {
                label: event.categoryName,
                to: `/archive?category=${event.category}`,
              },
              { label: event.title },
            ]}
          />

          <div className="mt-6 max-w-4xl">
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to={`/archive?category=${event.category}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-200"
              >
                <i className="ri-price-tag-3-line" />
                {event.categoryName}
              </Link>
              <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                <i className="ri-information-line" />
                {event.status}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1 text-xs text-accent-900">
                <i className="ri-pulse-line" />
                热度 {event.heat}
              </span>
            </div>

            <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
              {event.title}
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              {event.summary}
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500 md:text-sm">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                {event.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line" />
                {event.views} 阅读
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" />
                约 {event.readTime}
              </span>
              <button
                type="button"
                onClick={copyLink}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-xs transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                <i className={copied ? "ri-check-line" : "ri-link"} />
                {copied ? "链接已复制" : "复制档案链接"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1fr_300px] lg:gap-14">
          <div className="min-w-0">
            <figure className="overflow-hidden rounded-lg border border-background-200">
              <div className="relative h-56 w-full bg-secondary-100 md:h-96">
                <img
                  src={event.cover}
                  alt={`${event.title} - 爆料专区事件档案配图`}
                  title={`${event.title} - 爆料专区`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <figcaption className="flex items-center justify-between gap-3 bg-background-100 px-4 py-3 text-xs text-foreground-500">
                <span>{event.title} · 配图仅为氛围示意</span>
                <span className="hidden sm:inline">爆料专区档案</span>
              </figcaption>
            </figure>

            <div className="mt-8 space-y-5">
              <h2 className="font-heading text-xl font-bold text-foreground-950">事件经过</h2>
              {event.content.map((paragraph) => (
                <p
                  key={paragraph.slice(0, 16)}
                  className="text-base leading-[1.95] text-foreground-700"
                >
                  {paragraph}
                </p>
              ))}
            </div>

            <section className="mt-10 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <i className="ri-node-tree text-primary-500" />
                关键时间线
              </h2>
              <ol className="mt-5 space-y-0">
                {event.timeline.map((node, index) => (
                  <li key={`${node.date}-${index}`} className="relative flex gap-4 pb-6 last:pb-0">
                    <span className="relative flex w-4 shrink-0 justify-center">
                      {index !== event.timeline.length - 1 && (
                        <span className="absolute top-3 h-full w-px bg-background-300" />
                      )}
                      <span
                        className={`relative mt-1 h-3 w-3 rounded-full ring-4 ring-background-100 ${
                          index === 0 ? "bg-primary-500" : "bg-secondary-400"
                        }`}
                      />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="font-heading text-sm font-bold text-foreground-900">
                        {node.date}
                      </span>
                      <span className="mt-1 block text-sm leading-relaxed text-foreground-600">
                        {node.text}
                      </span>
                    </span>
                  </li>
                ))}
              </ol>
            </section>

            <div className="mt-8 flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-500">相关标签</span>
              {event.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/archive?q=${encodeURIComponent(tag)}`}
                  className="rounded-full bg-background-100 px-3 py-1.5 text-xs text-foreground-700 ring-1 ring-background-200 transition-colors hover:bg-primary-50 hover:text-primary-700"
                >
                  #{tag}
                </Link>
              ))}
            </div>

            <aside className="mt-10 rounded-lg border-l-4 border-accent-500 bg-accent-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-accent-900">
                <i className="ri-shield-check-line" />
                内容说明
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                本篇档案依据公开传播的报道与各方声明整理，统一采用匿名化叙述，时间节点以公开信息为准。事件后续如有新的公开进展，我们会补充更新到本页，请以最新版本为准。
              </p>
            </aside>
          </div>

          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">档案速览</h2>
              <dl className="mt-4 space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-500">主题分类</dt>
                  <dd className="font-medium text-foreground-900">{event.categoryName}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-500">发生年份</dt>
                  <dd className="font-medium text-foreground-900">{event.year} 年</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-500">记录日期</dt>
                  <dd className="font-medium text-foreground-900">{event.date}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-500">当前状态</dt>
                  <dd className="font-medium text-foreground-900">{event.status}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-500">热度值</dt>
                  <dd className="font-medium text-primary-600">{event.heat}</dd>
                </div>
              </dl>
              <Link
                to="/archive"
                className="mt-5 inline-flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-foreground-950 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-foreground-800"
              >
                <i className="ri-archive-2-line" />
                返回档案库
              </Link>
            </div>

            {related.length > 0 && (
              <div className="mt-5 rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">相关档案</h2>
                <div className="mt-3">
                  {related.map((item) => (
                    <EventCard key={item.id} event={item} variant="compact" />
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>

        {related.length > 0 && (
          <section className="mt-14 border-t border-background-200 pt-10">
            <div className="flex items-end justify-between gap-4">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                看完这篇，也可以接着看
              </h2>
              <Link
                to="/archive"
                className="hidden whitespace-nowrap text-sm font-medium text-primary-600 transition-opacity hover:opacity-80 sm:inline-flex sm:items-center sm:gap-1"
              >
                更多档案
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((item) => (
                <EventCard key={`grid-${item.id}`} event={item} />
              ))}
            </div>
          </section>
        )}
      </div>
    </article>
  );
}